import { useCallback, useEffect, useReducer } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import {
  isNormalizedRebalancingPlanGenerating,
  isNormalizedRebalancingPlanInGeneratedState,
  useCorrectExecuteRebalancePlanMutation,
  useCorrectGenerateRebalancePlanMutation,
  useCorrectRebalancePlanQuery,
  useIsKentRebalancingMode,
} from '@cast/split-me/features/cluster/rebalance';
import {
  getPoliciesV2QueryOptions,
  useUpdatePoliciesV2Mutation,
} from '@cast/shared-cluster-autoscaler-data-access';
import {
  GenerateRebalancingPlanCombinedParams,
  NormalizedRebalancingPlan,
} from '@cast/types';
import { notReachable, toNumOrUndefined } from '@cast/utils';

import { secondsToPolicyDuration } from '@cast/split-me/features/cluster/autoscaler/utils';

import {
  OPTIMIZATION_GOAL_TO_EMPTY_NODE_TTL_SECONDS,
  OPTIMIZATION_GOAL_TO_GENERATE_AUTOSCALER_REBALANCER_PLAN_PARAMS,
  OPTIMIZATION_GOAL_TO_GENERATE_KENT_REBALANCER_PLAN_PARAMS,
} from '../../_constants';
import { RebalancingPlanFlowState } from '../../_types';
import { OptimizationGoal } from '../../open-api-types/autopilot-types';

import isNumber from 'lodash/isNumber';
import {
  useNodesEligibilityCheck,
  NodesEligibilityCheckResult,
} from './useNodesEligibilityCheck';

type UseRebalancingPlanFlowResult = {
  state: RebalancingPlanFlowState;
  generateRebalancingPlan: (optimizationGoal: OptimizationGoal) => void;
  resetState: () => void;
  executeRebalancingPlan: () => Promise<boolean>;
};

const hasPositiveRebalancingPlanSavings = (
  plan: NormalizedRebalancingPlan
): boolean => {
  const savingsPercentage = toNumOrUndefined(
    plan.configurations.diff?.savingsPercentage
  );
  return isNumber(savingsPercentage) && savingsPercentage > 0;
};

type NodeRebalancePlanPhase =
  | { type: 'idle' }
  | { type: 'checking-nodes-eligibility'; optimizationGoal: OptimizationGoal }
  | { type: 'requesting-plan' }
  | { type: 'generating-plan'; rebalancingPlanId: string }
  | { type: 'plan-generation-error'; error: unknown }
  | {
      type: 'plan-generated';
      plan: NormalizedRebalancingPlan;
    };

type NodeRebalancePlanState = {
  // Run identity: bumped only by the command actions (generate/reset) so the
  // reducer can drop results of superseded runs
  generationRunId: number;
  phase: NodeRebalancePlanPhase;
};

type NodeRebalancePlanAction =
  | { type: 'GENERATE_REBALANCING_PLAN'; optimizationGoal: OptimizationGoal }
  | { type: 'RESET_REBALANCING_PLAN' }
  | { type: 'PLAN_REQUEST_STARTED'; generationRunId: number }
  | {
      type: 'PLAN_REQUEST_SUCCEEDED';
      generationRunId: number;
      rebalancingPlanId: string;
    }
  | { type: 'PLAN_REQUEST_FAILED'; generationRunId: number; error: unknown }
  | {
      type: 'PLAN_GENERATION_SUCCEEDED';
      generationRunId: number;
      plan: NormalizedRebalancingPlan;
    }
  | { type: 'PLAN_GENERATION_FAILED'; generationRunId: number; error: unknown };

const nodeRebalancePlanReducer = (
  state: NodeRebalancePlanState,
  action: NodeRebalancePlanAction
): NodeRebalancePlanState => {
  switch (action.type) {
    case 'GENERATE_REBALANCING_PLAN':
      return {
        generationRunId: state.generationRunId + 1,
        phase: {
          type: 'checking-nodes-eligibility',
          optimizationGoal: action.optimizationGoal,
        },
      };
    case 'RESET_REBALANCING_PLAN':
      return {
        generationRunId: state.generationRunId + 1,
        phase: { type: 'idle' },
      };
    case 'PLAN_REQUEST_STARTED':
      if (
        action.generationRunId !== state.generationRunId ||
        state.phase.type !== 'checking-nodes-eligibility'
      ) {
        return state;
      }
      return {
        generationRunId: action.generationRunId,
        phase: { type: 'requesting-plan' },
      };
    case 'PLAN_REQUEST_SUCCEEDED':
      if (
        action.generationRunId !== state.generationRunId ||
        state.phase.type !== 'requesting-plan'
      ) {
        return state;
      }
      return {
        generationRunId: action.generationRunId,
        phase: {
          type: 'generating-plan',
          rebalancingPlanId: action.rebalancingPlanId,
        },
      };
    case 'PLAN_REQUEST_FAILED':
      if (
        action.generationRunId !== state.generationRunId ||
        state.phase.type !== 'requesting-plan'
      ) {
        return state;
      }
      return {
        generationRunId: action.generationRunId,
        phase: { type: 'plan-generation-error', error: action.error },
      };
    case 'PLAN_GENERATION_SUCCEEDED':
      if (
        action.generationRunId !== state.generationRunId ||
        state.phase.type !== 'generating-plan'
      ) {
        return state;
      }
      return {
        generationRunId: action.generationRunId,
        phase: {
          type: 'plan-generated',
          plan: action.plan,
        },
      };
    case 'PLAN_GENERATION_FAILED':
      if (
        action.generationRunId !== state.generationRunId ||
        state.phase.type !== 'generating-plan'
      ) {
        return state;
      }
      return {
        generationRunId: action.generationRunId,
        phase: { type: 'plan-generation-error', error: action.error },
      };
    default:
      return notReachable(action);
  }
};

const mapInternalStateToRebalancingPlanState = ({
  phase,
  nodesEligibilityCheckResult,
}: {
  phase: NodeRebalancePlanPhase;
  nodesEligibilityCheckResult: NodesEligibilityCheckResult | undefined;
}): RebalancingPlanFlowState => {
  switch (phase.type) {
    case 'idle':
      return { type: 'idle' };
    case 'checking-nodes-eligibility': {
      switch (nodesEligibilityCheckResult?.status) {
        case undefined:
        case 'ready':
        case 'checking':
          return { type: 'nodes-eligibility-check' };
        case 'failed':
          return {
            type: 'nodes-eligibility-check-error',
            error: nodesEligibilityCheckResult.error,
          };
        case 'no-eligible-nodes':
          return { type: 'no-eligible-nodes' };

        default:
          return notReachable(nodesEligibilityCheckResult);
      }
    }
    case 'requesting-plan':
    case 'generating-plan':
      return {
        type: 'generating-plan',
      };
    case 'plan-generation-error':
      return {
        type: 'plan-generation-error',
        error: phase.error,
      };
    case 'plan-generated':
      return {
        type: 'plan-generated',
        plan: phase.plan,
        hasPositiveSavings: hasPositiveRebalancingPlanSavings(phase.plan),
      };

    default:
      return notReachable(phase);
  }
};

export const useRebalancingPlanFlow = ({
  clusterId,
}: {
  clusterId: string;
}): UseRebalancingPlanFlowResult => {
  const [state, dispatch] = useReducer(nodeRebalancePlanReducer, {
    generationRunId: 0,
    phase: { type: 'idle' },
  });

  const isKentRebalancingMode = useIsKentRebalancingMode();
  const queryClient = useQueryClient();
  const updatePoliciesMutation = useUpdatePoliciesV2Mutation();
  const { mutateAsync: generateRebalancingPlanMutationAsync } =
    useCorrectGenerateRebalancePlanMutation(clusterId);
  const executeRebalancePlanMutation = useCorrectExecuteRebalancePlanMutation();

  const nodesEligibilityCheckResult = useNodesEligibilityCheck({
    clusterId,
    enabled: state.phase.type === 'checking-nodes-eligibility',
    optimizationGoal:
      state.phase.type === 'checking-nodes-eligibility'
        ? state.phase.optimizationGoal
        : undefined,
  });

  const derivedState = mapInternalStateToRebalancingPlanState({
    phase: state.phase,
    nodesEligibilityCheckResult,
  });

  const { normalizedRebalancingPlan, isError: isRebalancingPlanQueryError } =
    useCorrectRebalancePlanQuery({
      clusterId,
      rebalancePlanId:
        state.phase.type === 'generating-plan'
          ? state.phase.rebalancingPlanId
          : undefined,
      includeConfigurations: true,
      enabled: state.phase.type === 'generating-plan',
    });

  useEffect(() => {
    if (
      state.phase.type !== 'checking-nodes-eligibility' ||
      nodesEligibilityCheckResult?.status !== 'ready'
    ) {
      return;
    }

    const { optimizationGoal } = state.phase;
    const { generationRunId } = state;

    const generatePlan = async () => {
      dispatch({
        type: 'PLAN_REQUEST_STARTED',
        generationRunId,
      });
      try {
        if (!isKentRebalancingMode) {
          // Fetch the latest policies right before writing so the PATCH carries
          // the current `version` for optimistic locking, bypassing staleTime.
          const existingPolicies = await queryClient.fetchQuery(
            getPoliciesV2QueryOptions({
              params: { clusterId },
              options: { staleTime: 0 },
            })
          );

          await updatePoliciesMutation.mutateAsync({
            clusterId,
            policiesV2: {
              version: existingPolicies?.version,
              enabled: true,
              unschedulablePods: { enabled: true },
              nodeDownscaler: {
                emptyNodesEnabled: true,
                emptyNodesDelay: secondsToPolicyDuration(
                  OPTIMIZATION_GOAL_TO_EMPTY_NODE_TTL_SECONDS[optimizationGoal]
                ),
              },
            },
          });
        }

        const kentGenerateRebalancingPresetPlanParams =
          OPTIMIZATION_GOAL_TO_GENERATE_KENT_REBALANCER_PLAN_PARAMS[
            optimizationGoal
          ];
        const autoscalerGenerateRebalancingPresetPlanParams =
          OPTIMIZATION_GOAL_TO_GENERATE_AUTOSCALER_REBALANCER_PLAN_PARAMS[
            optimizationGoal
          ];

        const eligibleNodes = nodesEligibilityCheckResult.nodes;

        const baseParams: GenerateRebalancingPlanCombinedParams =
          isKentRebalancingMode
            ? {
                ...kentGenerateRebalancingPresetPlanParams,
                rebalancingNodeNames: eligibleNodes.map((node) => ({
                  nodeName: node.nodeName,
                })),
              }
            : {
                ...autoscalerGenerateRebalancingPresetPlanParams,
                rebalancingNodes: eligibleNodes.map((node) => ({
                  nodeId: node.castNodeId,
                })),
              };

        const { rebalancingPlanId } =
          await generateRebalancingPlanMutationAsync(baseParams);

        if (!rebalancingPlanId) {
          dispatch({
            type: 'PLAN_REQUEST_FAILED',
            generationRunId,
            error: 'Failed to generate rebalancing plan.',
          });
          return;
        }

        dispatch({
          type: 'PLAN_REQUEST_SUCCEEDED',
          generationRunId,
          rebalancingPlanId,
        });
      } catch (error) {
        dispatch({
          type: 'PLAN_REQUEST_FAILED',
          generationRunId,
          error,
        });
      }
    };

    generatePlan();
  }, [
    clusterId,
    isKentRebalancingMode,
    queryClient,
    updatePoliciesMutation,
    generateRebalancingPlanMutationAsync,
    state,
    nodesEligibilityCheckResult,
  ]);

  useEffect(() => {
    if (state.phase.type !== 'generating-plan') {
      return;
    }

    if (normalizedRebalancingPlan) {
      if (
        isNormalizedRebalancingPlanInGeneratedState(normalizedRebalancingPlan)
      ) {
        dispatch({
          type: 'PLAN_GENERATION_SUCCEEDED',
          generationRunId: state.generationRunId,
          plan: normalizedRebalancingPlan,
        });
        return;
      }

      // if plan is not in "generating" state, it means it failed or was skipped
      // so we need to dispatch a failure, otherwise we'll wait forever
      if (!isNormalizedRebalancingPlanGenerating(normalizedRebalancingPlan)) {
        dispatch({
          type: 'PLAN_GENERATION_FAILED',
          generationRunId: state.generationRunId,
          error: 'Failed to generate rebalancing plan.',
        });
      }
    }

    if (isRebalancingPlanQueryError) {
      dispatch({
        type: 'PLAN_GENERATION_FAILED',
        generationRunId: state.generationRunId,
        error: 'Failed to generate rebalancing plan.',
      });
      return;
    }
  }, [normalizedRebalancingPlan, isRebalancingPlanQueryError, state]);

  const generateRebalancingPlan = useCallback(
    (nextOptimizationGoal: OptimizationGoal) => {
      // Retrying a failed eligibility check: the query is still enabled on the same
      // key with a cached error, so re-dispatching alone won't refetch.
      // Force a fresh fetch and guard on the checking phase so we only retry an in-flight failed check
      // reusing its optimizationGoal.
      if (
        state.phase.type === 'checking-nodes-eligibility' &&
        nodesEligibilityCheckResult.status === 'failed'
      ) {
        nodesEligibilityCheckResult.retry();
      }
      dispatch({
        type: 'GENERATE_REBALANCING_PLAN',
        optimizationGoal: nextOptimizationGoal,
      });
    },
    [state.phase.type, nodesEligibilityCheckResult]
  );

  const resetState = useCallback(() => {
    dispatch({ type: 'RESET_REBALANCING_PLAN' });
  }, []);

  const executeRebalancingPlan = useCallback(async (): Promise<boolean> => {
    if (state.phase.type !== 'plan-generated') {
      return false;
    }
    try {
      await executeRebalancePlanMutation.mutateAsync({
        clusterId,
        rebalancingPlanId: state.phase.plan.rebalancingPlanId!,
      });
      return true;
    } catch {
      return false;
    }
  }, [clusterId, executeRebalancePlanMutation, state]);

  return {
    state: derivedState,
    generateRebalancingPlan,
    resetState,
    executeRebalancingPlan,
  };
};
